import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { getSupabaseClient } from "../../../lib/supabaseClient";
import { usePhotographerAuth } from "../../../contexts/PhotographerAuthContext";
import PhotographerLayout from "../components/PhotographerLayout";
import type { Itinerary, ItineraryStatus, LegalDocument, LegalI18nLang } from "../../../store/types";
import { fetchLatestItineraryNotices } from "../../../api/itineraryRelayApi";

interface AssignmentItem {
  id: string;
  order_id: string;
  order_number: string;
  shoot_date: string;
  selected_city: string;
  service_name: string;
  number_of_reels: number;
  confirmation_status: string;
  itinerary: Itinerary | null;
  relayNotice: string | null;
}

const STATUS_LABELS: Record<string, { label: string; cls: string }> = {
  pending: { label: "已指派", cls: "bg-amber-100 text-amber-700" },
  confirmed: { label: "已確認", cls: "bg-green-100 text-green-700" },
  declined: { label: "已拒絕", cls: "bg-red-100 text-red-600" },
  replacement_needed: { label: "需替換", cls: "bg-orange-100 text-orange-700" },
};

const ITINERARY_STATUS_CONFIG: Record<ItineraryStatus, { label: string; cls: string }> = {
  draft: { label: "集合資訊準備中", cls: "bg-[#F0EDEA] text-[#7A6B5A]" },
  confirmed: { label: "集合資訊已確認", cls: "bg-emerald-50 text-emerald-700" },
  updated: { label: "集合資訊已更新", cls: "bg-teal-50 text-teal-700" },
  cancelled: { label: "行程已取消", cls: "bg-red-50 text-red-600" },
};

function formatLocalTime(iso: string | null): string {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("zh-TW", {
      year: "numeric", month: "2-digit", day: "2-digit",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
}

// ─── LegalAcceptanceGate ──────────────────────────────────────────────────────

function LegalAcceptanceGate({
  photographerId,
  onAllAccepted,
}: {
  photographerId: string;
  onAllAccepted: () => void;
}) {
  const [docs, setDocs] = useState<LegalDocument[]>([]);
  const [acceptedIds, setAcceptedIds] = useState<Set<string>>(new Set());
  const [checkedIds, setCheckedIds] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function load() {
      try {
        const supabase = getSupabaseClient();
        const [docsRes, logsRes] = await Promise.all([
          supabase
            .from("legal_documents")
            .select("*")
            .contains("required_for", ["photographer_onboarding"])
            .eq("is_active", true)
            .order("display_order", { ascending: true }),
          supabase
            .from("legal_acceptance_logs")
            .select("document_id")
            .eq("actor_type", "photographer")
            .eq("actor_id", photographerId),
        ]);

        const fetchedDocs = (docsRes.data as LegalDocument[]) ?? [];
        const existingIds = new Set(
          ((logsRes.data ?? []) as { document_id: string }[]).map((r) => r.document_id)
        );

        setDocs(fetchedDocs);
        setAcceptedIds(existingIds);

        // If all mandatory already accepted, unlock immediately
        const mandatory = fetchedDocs.filter((d) => d.is_mandatory);
        if (mandatory.length === 0 || mandatory.every((d) => existingIds.has(d.id))) {
          onAllAccepted();
        }
      } catch {
        // If fetch fails, don't block dashboard
        onAllAccepted();
      } finally {
        setLoading(false);
      }
    }
    void load();
  }, [photographerId, onAllAccepted]);

  const toggleCheck = (id: string) => {
    setCheckedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const pendingDocs = docs.filter((d) => !acceptedIds.has(d.id));
  const allPendingChecked = pendingDocs
    .filter((d) => d.is_mandatory)
    .every((d) => checkedIds.has(d.id));

  const handleSubmit = async () => {
    if (!allPendingChecked) {
      setError("請勾選所有必填條款");
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const supabase = getSupabaseClient();
      const toInsert = Array.from(checkedIds)
        .filter((id) => !acceptedIds.has(id))
        .map((docId) => ({
          actor_type: "photographer",
          actor_id: photographerId,
          document_id: docId,
          accepted_at: new Date().toISOString(),
          user_agent: navigator.userAgent,
        }));

      if (toInsert.length > 0) {
        const { error: insertErr } = await supabase.from("legal_acceptance_logs").insert(toInsert);
        if (insertErr) throw insertErr;
      }
      onAllAccepted();
    } catch (e) {
      setError(e instanceof Error ? e.message : "提交失敗，請重試");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return (
    <div className="flex items-center gap-2 px-5 py-6 bg-white rounded-xl border border-[#D9D9D9]/60 text-sm font-light text-[#2C2C2C]/40">
      <div className="w-4 h-4 flex items-center justify-center">
        <i className="ri-loader-4-line animate-spin text-base" />
      </div>
      載入條款...
    </div>
  );

  if (pendingDocs.length === 0) return null;

  return (
    <div className="bg-white rounded-xl border border-amber-200/60 overflow-hidden mb-6">
      {/* Gate header */}
      <div className="px-5 py-4 bg-amber-50 border-b border-amber-100 flex items-start gap-3">
        <div className="w-8 h-8 flex items-center justify-center bg-amber-100 rounded-full shrink-0 mt-0.5">
          <i className="ri-shield-check-line text-amber-700 text-base" />
        </div>
        <div>
          <p className="text-sm font-light text-amber-900">服務啟用前，請確認以下條款</p>
          <p className="text-xs font-light text-amber-600 mt-0.5">
            請閱讀並勾選所有必填文件，確認後即可查看您的指派任務
          </p>
        </div>
      </div>

      {/* Doc list */}
      <div className="divide-y divide-[#D9D9D9]/30 px-5">
        {pendingDocs.map((doc) => {
          const title = doc.title_i18n["zh-TW" as LegalI18nLang]
            || doc.title_i18n["en-US" as LegalI18nLang]
            || "—";
          const content = doc.content_i18n["zh-TW" as LegalI18nLang]
            || doc.content_i18n["en-US" as LegalI18nLang]
            || "";
          const isChecked = checkedIds.has(doc.id);
          const isExpanded = expandedId === doc.id;

          return (
            <div key={doc.id} className="py-4">
              <label className="flex items-start gap-3 cursor-pointer group">
                <div className="mt-0.5 shrink-0">
                  <input type="checkbox" checked={isChecked}
                    onChange={() => toggleCheck(doc.id)}
                    className="w-4 h-4 accent-[#2C2C2C] cursor-pointer" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-light text-[#2C2C2C]">我已閱讀並同意 {title}</span>
                    {doc.is_mandatory && (
                      <span className="px-1.5 py-0.5 bg-rose-50 text-rose-600 rounded text-[10px] font-light">必填</span>
                    )}
                  </div>
                  <div className="flex items-center gap-3 mt-1 text-[11px] text-[#2C2C2C]/30">
                    <span>版本 {doc.version}</span>
                    {doc.effective_date && <span>生效日期 {doc.effective_date}</span>}
                    {content && (
                      <button type="button"
                        onClick={(e) => { e.preventDefault(); setExpandedId(isExpanded ? null : doc.id); }}
                        className="text-[#C8B8A6] hover:text-[#B0A090] transition-colors cursor-pointer hover:underline whitespace-nowrap">
                        {isExpanded ? "收起" : "閱讀條款"}
                      </button>
                    )}
                  </div>
                </div>
              </label>

              {isExpanded && content && (
                <div className="ml-7 mt-3 px-4 py-3 bg-[#F7F6F3] rounded-lg border border-[#D9D9D9]/40">
                  <p className="text-xs font-light text-[#2C2C2C]/60 leading-relaxed whitespace-pre-line">{content}</p>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Submit */}
      <div className="px-5 py-4 border-t border-[#D9D9D9]/40 flex items-center justify-between gap-4">
        {error && (
          <p className="text-xs font-light text-rose-600 flex items-center gap-1.5">
            <i className="ri-error-warning-line text-sm" />
            {error}
          </p>
        )}
        {!error && <div />}
        <button
          type="button"
          onClick={handleSubmit}
          disabled={submitting || !allPendingChecked}
          className="px-5 py-2 bg-[#2C2C2C] text-white text-xs tracking-widest uppercase rounded-md hover:bg-[#2C2C2C]/80 transition-colors disabled:opacity-40 cursor-pointer whitespace-nowrap">
          {submitting ? "確認中..." : "確認並啟用"}
        </button>
      </div>
    </div>
  );
}

// ─── Main Dashboard ───────────────────────────────────────────────────────────

export default function PhotographerDashboardPage() {
  const { session, isLoading: authLoading } = usePhotographerAuth();
  const navigate = useNavigate();
  const [assignments, setAssignments] = useState<AssignmentItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [legalUnlocked, setLegalUnlocked] = useState(false);

  useEffect(() => {
    if (!authLoading && !session) navigate("/photographer/login", { replace: true });
  }, [session, authLoading, navigate]);

  const handleLegalAccepted = useCallback(() => {
    setLegalUnlocked(true);
  }, []);

  const fetchAssignments = useCallback(async () => {
    if (!session) return;
    setLoading(true);
    setError(null);
    try {
      const supabase = getSupabaseClient();
      const { data: bcs, error: bcErr } = await supabase
        .from("booking_confirmations")
        .select("id,order_id,confirmation_status")
        .eq("photographer_id", session.id)
        .order("created_at", { ascending: false });

      if (bcErr) throw bcErr;
      if (!bcs || bcs.length === 0) { setAssignments([]); return; }

      const orderIds = bcs.map((bc: { order_id: string }) => bc.order_id);
      const [ordersRes, itinRes] = await Promise.all([
        supabase.from("orders")
          .select("id,order_number,shoot_date,selected_city,number_of_reels,service_id")
          .in("id", orderIds),
        supabase.from("itineraries").select("*").in("order_id", orderIds),
      ]);

      if (ordersRes.error) throw ordersRes.error;

      const orderMap = new Map((ordersRes.data ?? []).map((o: {
        id: string; order_number: string; shoot_date: string; selected_city: string;
        number_of_reels: number; service_id: string;
      }) => [o.id, o]));
      const itinMap = new Map((itinRes.data ?? []).map((it: Itinerary) => [it.order_id, it]));

      // Fetch relay notices
      const relayNoticeMap = await fetchLatestItineraryNotices(orderIds, "photographer");

      const items: AssignmentItem[] = bcs.map((bc: { id: string; order_id: string; confirmation_status: string }) => {
        const o = orderMap.get(bc.order_id);
        return {
          id: bc.id,
          order_id: bc.order_id,
          order_number: o?.order_number ?? "—",
          shoot_date: o?.shoot_date ?? "—",
          selected_city: o?.selected_city ?? "—",
          service_name: "攝影服務",
          number_of_reels: o?.number_of_reels ?? 0,
          confirmation_status: bc.confirmation_status,
          itinerary: itinMap.get(bc.order_id) ?? null,
          relayNotice: relayNoticeMap.get(bc.order_id) ?? null,
        };
      });
      setAssignments(items);
    } catch (e) {
      setError(e instanceof Error ? e.message : "載入失敗");
    } finally {
      setLoading(false);
    }
  }, [session]);

  useEffect(() => {
    if (legalUnlocked) void fetchAssignments();
  }, [fetchAssignments, legalUnlocked]);

  if (authLoading) return null;

  return (
    <PhotographerLayout title="我的拍攝指派">
      {/* Legal acceptance gate — shown before assignments */}
      {session && !legalUnlocked && (
        <LegalAcceptanceGate
          photographerId={session.id}
          onAllAccepted={handleLegalAccepted}
        />
      )}

      {/* Assignments — only shown after legal cleared */}
      {legalUnlocked && (
        <>
          {loading ? (
            <div className="flex items-center justify-center h-48 text-[#2C2C2C]/40 text-sm font-light">載入中...</div>
          ) : error ? (
            <div className="flex flex-col items-center justify-center h-48 gap-3">
              <p className="text-sm text-red-500 font-light">{error}</p>
              <button onClick={fetchAssignments} className="text-sm text-[#2C2C2C]/60 underline cursor-pointer">重試</button>
            </div>
          ) : assignments.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64 gap-3 bg-white rounded-xl border border-[#D9D9D9]/60">
              <div className="w-10 h-10 flex items-center justify-center text-[#C8B8A6]/60">
                <i className="ri-calendar-line text-2xl" />
              </div>
              <p className="text-sm font-light text-[#2C2C2C]/40">目前尚無拍攝指派</p>
              <p className="text-xs font-light text-[#2C2C2C]/30">等待排班中，請耐心等候</p>
            </div>
          ) : (
            <div className="flex flex-col gap-3">
              {assignments.map((item) => {
                const statusInfo = STATUS_LABELS[item.confirmation_status] ?? { label: item.confirmation_status, cls: "bg-gray-100 text-gray-600" };
                const isExpanded = expandedId === item.id;
                return (
                  <div key={item.id} className="bg-white rounded-xl border border-[#D9D9D9]/60 overflow-hidden">
                    <button
                      onClick={() => setExpandedId(isExpanded ? null : item.id)}
                      className="w-full flex items-center gap-4 px-5 py-4 cursor-pointer hover:bg-[#F7F6F3]/60 transition-colors text-left"
                    >
                      <div className="shrink-0 w-12 text-center">
                        <div className="text-xs font-light text-[#2C2C2C]/40">{item.shoot_date.slice(0, 4)}</div>
                        <div className="text-base font-light text-[#2C2C2C]">{item.shoot_date.slice(5, 7)}/{item.shoot_date.slice(8, 10)}</div>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-0.5">
                          <span className="text-sm font-light text-[#2C2C2C]">{item.selected_city}</span>
                          <span className={`px-2 py-0.5 rounded-full text-[11px] font-light ${statusInfo.cls}`}>{statusInfo.label}</span>
                        </div>
                        <div className="flex items-center gap-3 text-xs font-light text-[#2C2C2C]/40">
                          <span>{item.service_name}</span>
                          <span>·</span>
                          <span>{item.number_of_reels > 0 ? `${item.number_of_reels} reel` : "攝影服務"}</span>
                        </div>
                      </div>
                      <div className="w-5 h-5 flex items-center justify-center text-[#2C2C2C]/30 shrink-0">
                        <i className={`ri-arrow-${isExpanded ? "up" : "down"}-s-line text-base`} />
                      </div>
                    </button>

                    {isExpanded && (
                      <div className="border-t border-[#D9D9D9]/40 px-5 py-4 flex flex-col gap-4">
                        <div className="flex items-start gap-2 bg-amber-50 border border-amber-200/50 rounded-lg px-3 py-2.5">
                          <div className="w-4 h-4 flex items-center justify-center text-amber-500 mt-0.5 shrink-0">
                            <i className="ri-shield-user-line text-sm" />
                          </div>
                          <div>
                            <p className="text-xs font-light text-amber-700">客戶聯繫</p>
                            <p className="text-xs font-light text-amber-600 mt-0.5">請透過平台聯繫 / Contact via platform</p>
                          </div>
                        </div>

                        {/* Relay system notice — secondary callout */}
                        {item.relayNotice && (
                          <div className="flex items-start gap-2 bg-teal-50 border border-teal-100 rounded-lg px-3 py-2.5">
                            <div className="w-4 h-4 flex items-center justify-center text-teal-500 mt-0.5 shrink-0">
                              <i className="ri-notification-3-line text-sm" />
                            </div>
                            <p className="text-xs font-light text-teal-700 leading-relaxed">{item.relayNotice}</p>
                          </div>
                        )}

                        <div className="grid grid-cols-2 gap-3 text-sm">
                          <div>
                            <p className="text-[11px] font-light text-[#2C2C2C]/40 mb-0.5">拍攝日期</p>
                            <p className="font-light text-[#2C2C2C]">{item.shoot_date}</p>
                          </div>
                          <div>
                            <p className="text-[11px] font-light text-[#2C2C2C]/40 mb-0.5">城市</p>
                            <p className="font-light text-[#2C2C2C]">{item.selected_city}</p>
                          </div>
                          <div>
                            <p className="text-[11px] font-light text-[#2C2C2C]/40 mb-0.5">客戶人數</p>
                            <p className="font-light text-[#2C2C2C]">{item.number_of_reels > 0 ? `${item.number_of_reels} 位` : "—"}</p>
                          </div>
                          <div>
                            <p className="text-[11px] font-light text-[#2C2C2C]/40 mb-0.5">服務類型</p>
                            <p className="font-light text-[#2C2C2C]">{item.service_name}</p>
                          </div>
                        </div>

                        {/* Official Itinerary — primary source */}
                        <div>
                          <div className="flex items-center gap-2 mb-3">
                            <h3 className="text-xs font-light text-[#2C2C2C]/50 uppercase tracking-wider">集合資訊</h3>
                            {item.itinerary && (
                              <span className={`px-2 py-0.5 rounded-full text-[10px] font-light ${ITINERARY_STATUS_CONFIG[item.itinerary.status as ItineraryStatus]?.cls ?? "bg-[#F0EDEA] text-[#7A6B5A]"}`}>
                                {ITINERARY_STATUS_CONFIG[item.itinerary.status as ItineraryStatus]?.label ?? item.itinerary.status}
                              </span>
                            )}
                          </div>
                          {item.itinerary ? (
                            <div className="flex flex-col gap-3">
                              <div>
                                <p className="text-[11px] font-light text-[#2C2C2C]/40 mb-0.5">集合地點</p>
                                <p className="text-sm font-light text-[#2C2C2C]">{item.itinerary.meeting_point || "—"}</p>
                              </div>
                              <div>
                                <p className="text-[11px] font-light text-[#2C2C2C]/40 mb-0.5">集合時間</p>
                                <p className="text-sm font-light text-[#2C2C2C]">{formatLocalTime(item.itinerary.meeting_time)}</p>
                              </div>
                              {item.itinerary.map_url && (
                                <div>
                                  <p className="text-[11px] font-light text-[#2C2C2C]/40 mb-1">地圖連結</p>
                                  <a href={item.itinerary.map_url} target="_blank" rel="noopener noreferrer"
                                    className="inline-flex items-center gap-1.5 text-sm font-light text-[#C8B8A6] hover:text-[#B0A090] transition-colors cursor-pointer">
                                    <div className="w-4 h-4 flex items-center justify-center">
                                      <i className="ri-map-pin-2-line text-sm" />
                                    </div>
                                    開啟地圖
                                  </a>
                                </div>
                              )}
                              {item.itinerary.notes && (
                                <div>
                                  <p className="text-[11px] font-light text-[#2C2C2C]/40 mb-0.5">拍攝說明</p>
                                  <p className="text-sm font-light text-[#2C2C2C]/70 leading-relaxed">{item.itinerary.notes}</p>
                                </div>
                              )}
                            </div>
                          ) : (
                            <div className="flex items-center gap-2 py-3 text-xs font-light text-[#2C2C2C]/30">
                              <div className="w-4 h-4 flex items-center justify-center">
                                <i className="ri-time-line text-sm" />
                              </div>
                              等待排班中，集合資訊即將更新
                            </div>
                          )}
                        </div>

                        <div className="pt-2 border-t border-[#D9D9D9]/30">
                          <p className="text-[11px] font-light text-[#2C2C2C]/30">訂單編號：{item.order_number}</p>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}
    </PhotographerLayout>
  );
}